import classNames from "classnames";
import { ProductQuantityProps } from "../BuilderInterface";
import "./style.scss";
import {
    getColorsFromInteractiveStates,
    getCompoundStyle,
    getStateColorsForBuilder,
    getStateStyleForBuilder,
    getStylesFromInteractiveStates
} from "../helpers";
import { NodeStates, ProductQuantityNode } from "../../build/types";
import DynamicIcon from "../DynamicIcon";
import { useInteractionStates } from "../../hooks/useInteractionStates";
import { formatTestId } from "../../utils/format";

const BuilderProductQuantity: React.FC<ProductQuantityProps> = ({
    node,
    actionCallbacks,
    builderProps,
    renderProps,
    disabled
}: ProductQuantityProps) => {
    const prQtyNode = node as ProductQuantityNode;
    const { id, attr, stateManager, buttonStyle, inputTypography } = prQtyNode;
    const { setElement: setWrapperElement, states: wrapperStates } = useInteractionStates<HTMLDivElement>();
    const { setElement: setDButtonElement, states: dButtonStates } = useInteractionStates<HTMLButtonElement>();
    const { setElement: setIButtonElement, states: iButtonStates } = useInteractionStates<HTMLButtonElement>();

    const productQuantityClass = classNames("product-quantity", {
        [`${attr?.className}`]: !!attr?.className
    });

    if (builderProps) {
        const { nodeState, selectedMenu, selectCallback, outlineCallback, onDragStart, onDragEnd } = builderProps;

        const decrementIcon = (
            <DynamicIcon
                name={prQtyNode.icons?.decrement?.src || "minus"}
                style={getCompoundStyle({
                    otherStyle: prQtyNode.icons?.decrement?.style,
                    stateStyle: getStateStyleForBuilder(stateManager, nodeState, "icons_decrement_style")
                })}
                colors={
                    getStateColorsForBuilder(stateManager, nodeState, "icons_decrement_colors") ||
                    prQtyNode.icons?.decrement?.colors
                }
            />
        );

        const incrementIcon = (
            <DynamicIcon
                name={prQtyNode.icons?.increment?.src || "add"}
                style={getCompoundStyle({
                    otherStyle: prQtyNode.icons?.increment?.style,
                    stateStyle: getStateStyleForBuilder(stateManager, nodeState, "icons_increment_style")
                })}
                colors={
                    getStateColorsForBuilder(stateManager, nodeState, "icons_increment_colors") ||
                    prQtyNode.icons?.increment?.colors
                }
            />
        );

        return (
            <div
                {...attr}
                className={productQuantityClass}
                style={getCompoundStyle({
                    node,
                    stateStyle: getStateStyleForBuilder(stateManager, nodeState, "attr_style")
                })}
                onClick={e => (selectCallback ? selectCallback(e, id) : {})}
                onMouseMove={e => (outlineCallback ? outlineCallback(e, id) : {})}
                id={id}
                onDragStart={e => onDragStart?.(e, id)}
                draggable={"true"}
                onDragEnd={onDragEnd}
            >
                <button
                    style={getCompoundStyle({
                        otherStyle: buttonStyle,
                        stateStyle: getStateStyleForBuilder(stateManager, nodeState, "buttonStyle")
                    })}
                    data-testid={formatTestId("builder-product-quantity-decrease-button")}
                >
                    {decrementIcon}
                </button>
                <input
                    type="text"
                    readOnly
                    value={1}
                    style={getCompoundStyle({
                        otherStyle: inputTypography,
                        stateStyle: getStateStyleForBuilder(stateManager, nodeState, "inputTypography")
                    })}
                    data-testid={formatTestId("builder-product-quantity-input")}
                />
                <button
                    style={getCompoundStyle({
                        otherStyle: buttonStyle,
                        stateStyle: getStateStyleForBuilder(stateManager, nodeState, "buttonStyle")
                    })}
                    data-testid={formatTestId("builder-product-quantity-increase-button")}
                >
                    {incrementIcon}
                </button>
                {selectedMenu}
            </div>
        );
    } else {
        const wrapperStyle = getStylesFromInteractiveStates({
            states: wrapperStates,
            defaultStyle: getCompoundStyle({ node }),
            stateManager,
            paths: ["attr_style"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const inputStyle = getStylesFromInteractiveStates({
            states: wrapperStates,
            defaultStyle: inputTypography,
            stateManager,
            paths: ["inputTypography"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        //Decrement button
        const dButtonStyle = getStylesFromInteractiveStates({
            states: dButtonStates,
            defaultStyle: buttonStyle,
            stateManager,
            paths: ["buttonStyle"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });
        const dIconStyle = getStylesFromInteractiveStates({
            states: dButtonStates,
            defaultStyle: prQtyNode.icons?.decrement?.style,
            stateManager,
            paths: ["icons_decrement_style"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });
        const dIconColors = getColorsFromInteractiveStates({
            states: dButtonStates,
            stateManager,
            paths: ["icons_decrement_colors"],
            defaultColors: prQtyNode.icons?.decrement?.colors,
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });
        const decrementIcon = (
            <DynamicIcon name={prQtyNode.icons?.decrement?.src || "minus"} style={dIconStyle} colors={dIconColors} />
        );

        //Increment button
        const iButtonStyle = getStylesFromInteractiveStates({
            states: iButtonStates,
            defaultStyle: buttonStyle,
            stateManager,
            paths: ["buttonStyle"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });
        const iIconStyle = getStylesFromInteractiveStates({
            states: iButtonStates,
            defaultStyle: prQtyNode.icons?.increment?.style,
            stateManager,
            paths: ["icons_increment_style"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });
        const iIconColors = getColorsFromInteractiveStates({
            states: iButtonStates,
            stateManager,
            paths: ["icons_increment_colors"],
            defaultColors: prQtyNode.icons?.increment?.colors,
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });
        const incrementIcon = (
            <DynamicIcon name={prQtyNode.icons?.increment?.src || "add"} style={iIconStyle} colors={iIconColors} />
        );

        return (
            <div {...attr} {...actionCallbacks} className={productQuantityClass} ref={setWrapperElement} style={wrapperStyle}>
                <button onClick={renderProps?.onDecrease} ref={setDButtonElement} style={dButtonStyle} disabled={disabled} data-testid={formatTestId("builder-product-quantity-decrease-button")}>
                    {decrementIcon}
                </button>
                <input
                    type="text"
                    className="state-product-quantity-input"
                    value={renderProps?.value || "0"}
                    style={inputStyle}
                    disabled={disabled}
                    onChange={e => renderProps?.onChange(e.target.value)}
                    onFocus={e => e.target.select()}
                    data-testid={formatTestId("builder-product-quantity-input")}
                />
                <button onClick={renderProps?.onIncrease} ref={setIButtonElement} style={iButtonStyle} disabled={disabled} data-testid={formatTestId("builder-product-quantity-increase-button")}>
                    {incrementIcon}
                </button>
            </div>
        );
    }
};

export default BuilderProductQuantity;
